/**
 * Ícones de interface, em SVG inline.
 *
 * Eram emoji: 🙈 e 👁 no botão de esconder níveis. Emoji não é ícone de
 * interface por três razões concretas, e não por gosto: o desenho é do sistema
 * operacional, então o mesmo botão é um macaco no Android, um olho vazado no
 * iOS e um quadrado vazio em algumas fontes de Linux; o tamanho não obedece ao
 * `font-size` de forma previsível, o que desalinha o glifo dentro do alvo de
 * 44px; e a cor é a do emoji, não a `currentColor` do tema — no cabeçalho
 * escuro o 👁 aparecia colorido no meio de controles acromáticos.
 *
 * Traço e não preenchimento, `currentColor`, `viewBox` de 24 e a espessura 2 do
 * Feather — que é a família que o mobile usa (`@expo/vector-icons/Feather`).
 * Assim os dois produtos têm o mesmo desenho de ícone, e um ícone novo aqui tem
 * uma referência a seguir em vez de virar decisão de estilo a cada vez.
 *
 * `aria-hidden` em todos: o rótulo acessível é do botão em volta, e um ícone que
 * também se anuncia faz o leitor de tela dizer a mesma coisa duas vezes.
 */

type IconProps = {
  /** Lado do quadrado, em px. Padrão 20 — cabe no alvo de 44px com folga. */
  size?: number;
  className?: string;
};

function Svg({ size = 20, className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      aria-hidden
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  );
}

/** Níveis à vista. */
export function EyeIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

/**
 * Níveis escondidos: o olho cortado.
 *
 * O par olho / olho-cortado é a convenção que a pessoa já conhece de campo de
 * senha, e é o que o pedido de 30/07 descreve. O 🙈 dizia "não estou vendo" com
 * um bicho tapando os olhos, que é uma piada, não um estado de interface.
 */
export function EyeOffIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </Svg>
  );
}

/**
 * Conversa — o ícone do botão que abre o WhatsApp.
 *
 * É a bolha de conversa do Feather, e não a marca do WhatsApp, por decisão:
 * reproduzir logotipo de terceiro à mão é onde se erra a curva de um glifo que
 * todo mundo reconhece de cor, e um logotipo torto passa por descuido do
 * produto. O botão leva a palavra "WhatsApp" escrita ao lado e o verde da
 * marca, que é o que a pessoa procura na tela.
 */
export function ChatIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </Svg>
  );
}
